import { getUTMFieldsFromLocalStorage, stringifyUTMParams } from "@/shared/utils/utm.util";
import { useEffect, useRef, useState } from "react";

type ExitIntentPopupProps = {
  calendlyUrl: string;
};

const MOBILE_DELAY_MS = 15_000;
const SESSION_KEY = "graphy_popup_seen";

const bullets = [
  "AI-powered tools",
  "Branded website and mobile apps",
  "Support that actually responds",
];

const ExitIntentPopup = ({ calendlyUrl }: ExitIntentPopupProps) => {
  const [visible, setVisible] = useState(false);
  const firedRef = useRef(false);

  const fire = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    sessionStorage.setItem(SESSION_KEY, "1");
    setVisible(true);
  };

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;

    if (isMobile) {
      const timer = setTimeout(fire, MOBILE_DELAY_MS);
      return () => clearTimeout(timer);
    } else {
      const handleMouseLeave = (e: MouseEvent) => {
        if (e.clientY <= 0) fire();
      };
      document.addEventListener("mouseleave", handleMouseLeave);
      return () => document.removeEventListener("mouseleave", handleMouseLeave);
    }
  }, []);

  if (!visible) return null;

  const handleCta = () => {
    const utmFields = getUTMFieldsFromLocalStorage();
    utmFields["utm_content"] = "exit_popup";
    const utmParams = stringifyUTMParams(utmFields);
    const separator = calendlyUrl.includes("?") ? "&" : "?";
    window.open(`${calendlyUrl}${separator}${utmParams}`, "_blank")?.focus();
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      onClick={() => setVisible(false)}
    >
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Purple top panel */}
        <div className="relative bg-brandPrimary px-8 pt-10 pb-9">
          {/* Close */}
          <button
            onClick={() => setVisible(false)}
            aria-label="Close"
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M10 2L2 10M2 2l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>

          {/* Pill */}
          <span className="inline-block rounded-full bg-brandSecondary px-3.5 py-1 font-inter text-[11px] font-semibold uppercase tracking-widest text-neutralBlack">
            Limited offer
          </span>

          {/* Headline */}
          <h2 className="mt-4 font-inter text-[30px] font-medium leading-tight tracking-[-0.5px] text-white">
            Switch To Graphy Today &amp;{" "}
            <span className="text-brandSecondary font-bold text-[34px]">
              Save 25%
            </span>
          </h2>
        </div>

        {/* White bottom panel */}
        <div className="px-8 py-7">
          <p className="font-inter text-[14px] leading-[22px] text-gray-500">
            Thousands of creators have already made the switch — paying less and getting more with:
          </p>

          <ul className="mt-4 space-y-2.5">
            {bullets.map((b) => (
              <li key={b} className="flex items-center gap-3">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brandSecondary">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5l2.5 2.5L8 3" stroke="#232228" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="font-inter text-[14px] font-medium text-gray-700">{b}</span>
              </li>
            ))}
          </ul>

          <p className="mt-4 font-inter text-[14px] leading-[22px] text-gray-500">
            Book a free call and we&apos;ll show you exactly how much you&apos;ll save.
          </p>

          <button
            onClick={handleCta}
            className="mt-6 w-full rounded-full bg-brandPrimary px-6 py-4 font-inter text-[15px] font-medium text-white transition-opacity hover:opacity-90"
          >
            Talk To The Migration Expert
          </button>

          <p className="mt-3 text-center font-inter text-[12px] text-gray-400">
            No commitment · 30-minute call
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
